import { PeriodBar, financialYear } from '../../components/accounts/PeriodBar';
import { AccountSelect, type Account as PickerAccount } from '../../components/accounts/AccountSelect';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { date, money } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { JournalDrawer } from './JournalDrawer';

interface Account {
  id: string;
  code: string;
  name: string;
  nature: string;
  group_name: string;
}

interface LedgerRow {
  journal_date: string;
  journal_no: string;
  journal_type: string;
  source_doc_type: string;
  narration: string | null;
  debit: string;
  credit: string;
  journal_id: string;
  status: string;
  running_balance: string;
}

type Party = { id: string; name: string };

/**
 * The general ledger, and the customer and supplier ledgers beside it.
 *
 * Every line reaches the entry behind it, and every entry reaches the document
 * that raised it. An accountant looking at a figure wants the invoice, not the
 * reassurance that a number moved.
 */
export function LedgerPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<'ACCOUNT' | 'CUSTOMER' | 'SUPPLIER'>('ACCOUNT');
  const [accountId, setAccountId] = useState('');
  const [partyId, setPartyId] = useState('');
  const [from, setFrom] = useState(financialYear().from);
  const [to, setTo] = useState(financialYear().to);
  const [openJournal, setOpenJournal] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ data: Account[] }>('/accounts'),
    staleTime: 300_000,
  });
  const customers = useQuery({ queryKey: ['customers', ''], queryFn: () => endpoints.parties.customers() });
  const suppliers = useQuery({ queryKey: ['suppliers', ''], queryFn: () => endpoints.parties.suppliers() });

  const query = new URLSearchParams();
  if (from) query.set('from', from);
  if (to) query.set('to', to);

  const ledger = useQuery({
    queryKey: ['ledger', accountId, query.toString()],
    queryFn: () => api.get<{ opening: string; data: LedgerRow[] }>(`/accounts/${accountId}/ledger?${query.toString()}`),
    enabled: tab === 'ACCOUNT' && accountId !== '',
  });

  const partyLedger = useQuery({
    queryKey: ['partyLedger', tab, partyId],
    queryFn: () => api.get<{ balance: string; data: Record<string, string>[] }>(`/parties/${tab}/${partyId}/ledger`),
    enabled: tab !== 'ACCOUNT' && partyId !== '',
  });

  const accountList = accounts.data?.data ?? [];
  const parties =
    tab === 'SUPPLIER'
      ? ((suppliers.data?.data ?? []) as unknown as Party[])
      : ((customers.data?.data ?? []) as unknown as Party[]);

  const rows = useMemo(() => ledger.data?.data ?? [], [ledger.data]);

  const chosen = accountList.find((a) => a.id === accountId);

  // Opened from the accounts overview as /ledgers?code=1111, so a figure on a
  // card leads straight to the entries behind it.
  useEffect(() => {
    const code = params.get('code');
    if (!code || accountId) return;
    const match = accountList.find((a) => a.code === code);
    if (match) setAccountId(match.id);
  }, [params, accountList, accountId]);

  // What moved in the period, and where that leaves the account.
  const totals = rows.reduce(
    (a, r) => ({ debit: a.debit + Number(r.debit ?? 0), credit: a.credit + Number(r.credit ?? 0) }),
    { debit: 0, credit: 0 },
  );
  const closing = rows.length > 0 ? Number(rows[rows.length - 1]!.running_balance) : Number(ledger.data?.opening ?? 0);

  return (
    <>
      <PageHeader
        title="Ledgers"
        subtitle="Every figure reaches the entry behind it, and every entry reaches its document."
      />

      <div className="mb-4 flex gap-1 border-b border-ink/10">
        {(
          [
            ['ACCOUNT', 'General ledger'],
            ['CUSTOMER', 'Customers'],
            ['SUPPLIER', 'Suppliers'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            className={`px-3 py-2 text-sm ${
              tab === key ? 'border-b-2 border-gold-600 font-medium' : 'text-ink-soft hover:text-ink'
            }`}
            onClick={() => {
              setTab(key);
              setPartyId('');
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <ErrorNotice error={ledger.error ?? partyLedger.error} />

      <PeriodBar
        from={from}
        to={to}
        onChange={(r) => {
          setFrom(r.from);
          setTo(r.to);
        }}
      >
        <div className="min-w-[18rem] flex-1">
          <p className="label">{tab === 'ACCOUNT' ? 'Account' : tab === 'SUPPLIER' ? 'Supplier' : 'Customer'}</p>
          {tab === 'ACCOUNT' ? (
            <AccountSelect
              accounts={accountList as unknown as PickerAccount[]}
              value={accountId}
              onChange={setAccountId}
            />
          ) : (
            <select className="field w-full" value={partyId} onChange={(e) => setPartyId(e.target.value)}>
              <option value="">Choose {tab === 'SUPPLIER' ? 'a supplier' : 'a customer'}…</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </PeriodBar>

      {tab === 'ACCOUNT' && accountId === '' ? <Empty message="Choose an account to see its ledger." /> : null}
      {tab !== 'ACCOUNT' && partyId === '' ? (
        <Empty message={`Choose ${tab === 'SUPPLIER' ? 'a supplier' : 'a customer'} to see their account.`} />
      ) : null}

      {ledger.isLoading || partyLedger.isLoading ? <Loading /> : null}

      {tab === 'ACCOUNT' && ledger.data ? (
        <>
          {/* Opening, what moved, and where it ended — the four figures an
              accountant checks before reading a single line. */}
          <div className="mb-3 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-silver-100 bg-silver-100 md:grid-cols-4">
            <Figure
              label={`${chosen?.code ?? ''} ${chosen?.name ?? ''}`.trim() || 'Account'}
              value={`Opening ${money(ledger.data.opening)}`}
            />
            <Figure label="Debits" value={money(totals.debit.toFixed(2))} />
            <Figure label="Credits" value={money(totals.credit.toFixed(2))} />
            <Figure
              label="Closing"
              value={money(closing.toFixed(2))}
              strong
              hint={`${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`}
            />
          </div>

          {rows.length === 0 ? (
            <Empty message="Nothing posted to this account in that period." />
          ) : (
            <Table head={['Date', 'Voucher', 'Narration', 'Source', 'Debit', 'Credit', 'Balance']}>
              <tr className="bg-silver-50/60">
                <td className="td text-2xs uppercase tracking-wide text-ink-soft" colSpan={6}>
                  Opening balance
                </td>
                <td className="td num text-right font-medium">{money(ledger.data.opening)}</td>
              </tr>
              {rows.map((r, i) => (
                <tr
                  key={`${r.journal_id}-${i}`}
                  className="cursor-pointer hover:bg-gold-50"
                  onClick={() => setOpenJournal(r.journal_id)}
                >
                  <td className="td">{date(r.journal_date)}</td>
                  <td className="td num underline">{r.journal_no}</td>
                  <td className="td text-xs">{r.narration ?? '—'}</td>
                  <td className="td text-xs text-ink-soft">{r.source_doc_type.replace(/_/g, ' ').toLowerCase()}</td>
                  <td className="td num text-right">{Number(r.debit) > 0 ? money(r.debit) : ''}</td>
                  <td className="td num text-right">{Number(r.credit) > 0 ? money(r.credit) : ''}</td>
                  <td className="td num text-right font-medium">{money(r.running_balance)}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      ) : null}

      {tab !== 'ACCOUNT' && partyLedger.data ? (
        <>
          <div className="mb-2 flex justify-between text-sm">
            <span className="font-medium">{parties.find((p) => p.id === partyId)?.name}</span>
            <span>
              Balance <span className="num font-semibold">{money(partyLedger.data.balance)}</span>
            </span>
          </div>

          {partyLedger.data.data.length === 0 ? (
            <Empty message="Nothing on this account yet." />
          ) : (
            <Table head={['Date', 'Document', 'Narration', 'Debit', 'Credit', 'Balance']}>
              {partyLedger.data.data.map((r, i) => (
                <tr key={i}>
                  <td className="td">{date(r['entry_date'] ?? null)}</td>
                  <td className="td num">{r['doc_no'] ?? '—'}</td>
                  <td className="td text-xs">{r['narration'] ?? '—'}</td>
                  <td className="td num text-right">{Number(r['debit']) > 0 ? money(r['debit']) : ''}</td>
                  <td className="td num text-right">{Number(r['credit']) > 0 ? money(r['credit']) : ''}</td>
                  <td className="td num text-right font-medium">{money(r['balance_after'] ?? '0')}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      ) : null}

      {openJournal ? <JournalDrawer journalId={openJournal} onClose={() => setOpenJournal(null)} /> : null}
    </>
  );
}

function Figure({ label, value, hint, strong }: { label: string; value: string; hint?: string; strong?: boolean }) {
  return (
    <div className="bg-surface px-3 py-2">
      <p className="truncate text-2xs text-ink-soft">{label}</p>
      <p className={`num ${strong ? 'text-base font-bold' : 'font-medium'}`}>{value}</p>
      {hint ? <p className="text-2xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}
